import { useEffect, useState, type FormEvent } from "react";
import {
  PAYMENT_KIND_LABELS,
  PAYMENT_METHOD_LABELS,
  formatCad,
  type AppointmentView,
  type PaymentKind,
  type PaymentMethod,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { useToastStore } from "../../../core/stores/toast";
import { createPayment, getAppointment } from "../lib/api";
import { dateTimeLocalToIso, toDateTimeLocalValue } from "../lib/dates";
import { centsToInput, parseAmountToCents } from "../lib/money";

const FORM_ID = "atelier-payment-form";

const METHODS = Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[];
const KINDS = Object.keys(PAYMENT_KIND_LABELS) as PaymentKind[];

interface PaymentModalProps {
  open: boolean;
  appointment: AppointmentView;
  onClose: () => void;
  /** Reçoit la fiche rechargée (montants dérivés à jour). */
  onSaved: (view: AppointmentView) => void;
}

/**
 * Encaisser : montant pré-rempli avec le dû, type (acompte / solde /
 * remboursement), méthode, date et référence. Le paiement par carte en ligne
 * (Stripe) viendra plus tard : la méthode « en ligne » existe déjà mais se
 * saisit à la main pour l'instant.
 */
export function PaymentModal({ open, appointment: a, onClose, onSaved }: PaymentModalProps) {
  const push = useToastStore((s) => s.push);
  const [amount, setAmount] = useState("");
  const [kind, setKind] = useState<PaymentKind>("solde");
  const [method, setMethod] = useState<PaymentMethod>("carte");
  const [paidAtLocal, setPaidAtLocal] = useState("");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount(centsToInput(a.dueCents));
    // Premier encaissement d'un travail pas encore terminé : c'est un acompte.
    const done = ["termine", "pret", "recupere"].includes(a.status);
    setKind(a.payments.length === 0 && !done ? "acompte" : "solde");
    setMethod("carte");
    setPaidAtLocal(toDateTimeLocalValue(new Date().toISOString()));
    setReference("");
  }, [open, a]);

  const amountCents = parseAmountToCents(amount);
  const paidAt = paidAtLocal ? dateTimeLocalToIso(paidAtLocal) : null;
  const problem =
    amountCents === null
      ? "Montant illisible (ex. 150,00)."
      : amountCents === 0
        ? "Indiquez un montant."
        : paidAtLocal && !paidAt
          ? "Date illisible."
          : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (problem || saving || amountCents === null) return;
    setSaving(true);
    try {
      await createPayment(a.id, {
        amountCents,
        kind,
        method,
        paidAt: paidAt ?? undefined,
        reference: reference.trim() || null,
      });
      const view = await getAppointment(a.id);
      push(
        "success",
        kind === "remboursement" ? "Remboursement enregistré" : "Paiement encaissé",
        view.dueCents === 0 ? "Solde réglé." : `Reste dû : ${formatCad(view.dueCents)}`,
      );
      onSaved(view);
    } catch {
      push("danger", "Encaissement impossible", "Vérifiez les champs saisis.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Encaisser un paiement"
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={saving || !!problem}>
            {saving ? "Enregistrement…" : "Encaisser"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="atelier-form" onSubmit={submit}>
        <p className="payment-modal__due">
          Solde dû : <strong>{formatCad(a.dueCents)}</strong>
          {a.totalWithTaxCents > 0 ? ` sur ${formatCad(a.totalWithTaxCents)} TTC` : ""}
        </p>
        <div className="form-section__grid">
          <Field label="Montant" hint="En dollars, taxes incluses." error={problem ?? undefined}>
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
              required
            />
          </Field>
          <Field label="Type">
            <Select value={kind} onChange={(e) => setKind(e.target.value as PaymentKind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {PAYMENT_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Méthode">
            <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date">
            <Input
              type="datetime-local"
              value={paidAtLocal}
              onChange={(e) => setPaidAtLocal(e.target.value)}
            />
          </Field>
        </div>
        <div className="form-section__grid form-section__grid--full">
          <Field label="Référence" hint="Numéro de transaction, de chèque… (facultatif)">
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={120}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
